import { apiClient } from "@lobby/api-client";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../shared";

import type { SendOptions } from "@lobby/api-client";

export const Chat = {
  useConversationList() {
    const { isAuth } = useAuth();

    return useQuery({
      staleTime: 30_000,
      refetchInterval: 30_000,
      enabled: isAuth,
      queryKey: ["Conversation.getList"],
      queryFn: async () => {
        const response = await apiClient.send({
          endpoint: "Conversation.getList",
        });

        if (response.error) {
          return {
            ...response,
            result: [],
          };
        }

        return response;
      },
    });
  },
  useSendMessage() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["Conversation.sendMessage"],
      mutationFn: (params: NonNullable<SendOptions<"Conversation.sendMessage">["params"]>) =>
        apiClient.send({
          endpoint: "Conversation.sendMessage",
          params,
        }),
      onSettled: async () =>
        await queryClient.invalidateQueries({
          queryKey: ["Conversation.getMessages"],
        }),
    });
  },
  useUploadAttachment() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["Conversation.uploadAttachment"],
      mutationFn: async ({ conversationId, file }: { conversationId: number; file: File }) => {
        const type = file.type.startsWith("image/") ? "image" : "video";

        const uploadLinkResponse = await apiClient.send({
          endpoint: "Conversation.getAttachmentUpdLink",
          params: {
            conversationId,
            type,
          },
        });

        if (uploadLinkResponse.error) {
          throw new Error(uploadLinkResponse.error.message);
        }

        const uploadUrl = uploadLinkResponse.result.url;

        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const fileUrl = uploadUrl.split("?")[0];

        return { fileUrl, type };
      },
      onSettled: async () =>
        await queryClient.invalidateQueries({
          queryKey: ["Conversation.getMessages"],
        }),
    });
  },
  useConversationHistory(conversationId: number | null) {
    const { isAuth } = useAuth();

    return useInfiniteQuery({
      staleTime: 30_000,
      refetchInterval: 30_000,
      initialPageParam: 0,
      enabled: isAuth && conversationId !== null,
      queryKey: ["Conversation.getMessages", conversationId],
      queryFn: async ({ pageParam: seqNum }) => {
        const response = await apiClient.send({
          endpoint: "Conversation.getMessages",
          params: {
            conversationId: conversationId as number,
            startSeqNum: -seqNum,
            limit: 20,
          },
        });

        if (response.error) {
          response.result = response.result ?? [];
        }

        return response;
      },
      getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
        const lastPageResult = lastPage.result;
        const lastSeqNum = lastPageResult[lastPageResult.length - 1]?.seqNum;

        if (lastPageResult.length === 0 || lastSeqNum === 0) {
          return undefined;
        }

        return lastSeqNum;
      },
      getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
        if (firstPageParam <= 1) {
          return undefined;
        }
        return firstPageParam - 1;
      },
    });
  },
};
